import { Link, useNavigate, useParams } from 'react-router-dom'
import { useModals } from '@/app/modals/ModalContext'
import { Badge } from '@/components/Badge'
import { Banner } from '@/components/Banner'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { LinkButton } from '@/components/LinkButton'
import { Page, PageHeader } from '@/components/Page'
import { Spinner } from '@/components/Spinner'
import { CALLER_STATUS_META } from './api'
import { CallerFormFields, useCallerForm } from './CallerForm'

/**
 * An enquiry once it has been taken down — its status, and the route into a
 * patient record. Logging a *new* call happens in a dialog (`ModalHost`); this
 * page is where one is opened and worked afterwards.
 */
export function CallerFormScreen({ mode }: { mode: 'create' | 'edit' }) {
  const { callerId = '' } = useParams()
  const navigate = useNavigate()
  const modals = useModals()

  const state = useCallerForm({
    mode,
    callerId,
    onSaved: (saved) => navigate(`/callers/${saved.id}`, { replace: true }),
  })
  const { converted, existing } = state

  if (mode === 'edit' && state.loading) return <Spinner label="Loading enquiry" />

  return (
    <Page>
      <PageHeader
        eyebrow="Booking"
        title={mode === 'edit' ? state.form.full_name || 'Enquiry' : 'Log a call'}
        description={
          mode === 'edit'
            ? 'Everything taken down when they rang.'
            : 'Only the name is required — fill the rest in after the phone goes down.'
        }
        actions={
          mode === 'edit' && existing.data ? (
            <span className="flex flex-wrap items-center gap-3">
              <Badge tone={CALLER_STATUS_META[existing.data.status].tone}>
                {CALLER_STATUS_META[existing.data.status].label}
              </Badge>
              {converted && existing.data.converted_patient_id && (
                <LinkButton
                  to={`/patients/${existing.data.converted_patient_id}`}
                  variant="secondary"
                >
                  <Icon name="users" />
                  Open patient record
                </LinkButton>
              )}
              {!converted && (
                <Button onClick={() => modals.open({ kind: 'convert', callerId })}>
                  <Icon name="userPlus" />
                  Convert to patient
                </Button>
              )}
            </span>
          ) : null
        }
      />

      {state.error && <Banner tone="danger">{state.error}</Banner>}

      {converted && (
        <Banner tone="info">
          This enquiry became a patient, so it is kept as it was. Edit the patient record instead.
        </Banner>
      )}

      <form className="grid gap-4" onSubmit={state.submit}>
        <CallerFormFields state={state} />

        <div className="flex flex-wrap items-center justify-end gap-3">
          <Link to="/callers">Back to callers</Link>
          <span className="flex-1" />
          <Button type="submit" loading={state.save.isPending} disabled={converted}>
            <Icon name="save" />
            {mode === 'edit' ? 'Save enquiry' : 'Log call'}
          </Button>
        </div>
      </form>
    </Page>
  )
}
